// PRD §2.1 / §5 — right panel router. Picks Domain / SubArea / Item detail
// view based on the selected node.
//
// Slice 3 stubbed everything with EmptyDetail. Slice 4 wired DomainDetail.
// Slice 6 wired SubAreaDetail. Slice 7 wires ItemDetail (basic info + scope);
// later slices grow the item form.

import { useMemo } from 'react';

import { useProItemDesignPageStore } from '../store/pro-item-design-page.store';
import { useMergedProTree, useProItemQuery } from '../hooks/use-pro-tree';
import { buildDraftItem, isDraftId } from '../utils/draft-node';
import { DomainDetail } from './DomainDetail';
import { EmptyDetail } from './EmptyDetail';
import { ItemDetailContainer } from './ItemDetail';
import { NotFoundDetail } from './NotFoundDetail';
import { SubAreaDetail } from './SubAreaDetail';

export function ProDetailPanel() {
  const selected = useProItemDesignPageStore((s) => s.selected);
  const draft = useProItemDesignPageStore((s) => s.draft);
  // React Query dedupes — the panel and the view share one cached fetch (the
  // merge just layers the Add draft on top).
  const { tree } = useMergedProTree();

  // SHCT-388 — a draft item (negative id) has no server row yet, so never call
  // getItem for it; render a synthetic ProItem built from the draft instead.
  const draftItemId = selected?.type === 'item' && isDraftId(selected.id) ? selected.id : null;
  // Hook order must be stable across renders — call useProItemQuery
  // unconditionally with a nullable id; it short-circuits when null.
  const itemQuery = useProItemQuery(
    selected?.type === 'item' && draftItemId === null ? selected.id : null,
  );
  const draftItem = useMemo(
    () =>
      draft?.type === 'item' && draftItemId !== null
        ? buildDraftItem(draft.tempId, draft.domainId, draft.subAreaId, draft.name)
        : undefined,
    [draft, draftItemId],
  );

  let body: React.ReactNode = <EmptyDetail />;

  if (selected !== null) {
    if (selected.type === 'domain') {
      const idx = tree.findIndex((d) => d.id === selected.id);
      const domain = idx >= 0 ? tree[idx] : null;
      body = domain ? (
        <DomainDetail domain={domain} domainIndex={idx} />
      ) : (
        <NotFoundDetail />
      );
    } else if (selected.type === 'subArea') {
      const di = tree.findIndex((d) => d.id === selected.domainId);
      const domain = di >= 0 ? tree[di] : null;
      const si = domain ? domain.subAreas.findIndex((s) => s.id === selected.id) : -1;
      const subArea = domain && si >= 0 ? domain.subAreas[si] : null;
      body =
        domain && subArea ? (
          <SubAreaDetail
            domain={domain}
            domainIndex={di}
            subArea={subArea}
            subAreaIndex={si}
          />
        ) : (
          <NotFoundDetail />
        );
    } else {
      const di = tree.findIndex((d) => d.id === selected.domainId);
      const domain = di >= 0 ? tree[di] : null;
      const si = domain ? domain.subAreas.findIndex((s) => s.id === selected.subAreaId) : -1;
      const subArea = domain && si >= 0 ? domain.subAreas[si] : null;
      const ii = subArea ? subArea.items.findIndex((it) => it.id === selected.id) : -1;
      // For a draft item the row lives only in the merged tree + `draftItem`;
      // skip the query loading/error flags entirely.
      const isDraft = draftItemId !== null;
      body =
        domain && subArea && ii >= 0 ? (
          <ItemDetailContainer
            domain={domain}
            domainIndex={di}
            subArea={subArea}
            subAreaIndex={si}
            itemId={selected.id}
            itemIndex={ii}
            item={isDraft ? draftItem : itemQuery.data}
            isLoading={isDraft ? false : itemQuery.isLoading}
            isError={isDraft ? false : itemQuery.isError}
          />
        ) : (
          <NotFoundDetail />
        );
    }
  }

  return (
    <div
      className="card"
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>{body}</div>
    </div>
  );
}
